import { Button } from "@/project/karuna/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/karuna/components/ui/select";
import { cn } from "@/project/karuna/lib/utils";
import {
  KARUNA_PAGE_SIZE_OPTIONS,
  type KarunaPageSize,
} from "@/project/karuna/constants/listPagination";
import { ChevronLeft, ChevronRight, LayoutGrid, List } from "lucide-react";

export type KarunaViewMode = "card" | "list";

export function ListCardViewToggle({
  viewMode,
  onViewChange,
  className,
}: {
  viewMode: KarunaViewMode;
  onViewChange: (v: KarunaViewMode) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "inline-flex rounded-lg border border-border bg-muted/40 p-1",
        className
      )}
      role="group"
      aria-label="View mode"
    >
      <Button
        type="button"
        variant={viewMode === "card" ? "secondary" : "ghost"}
        size="sm"
        className="gap-2"
        onClick={() => onViewChange("card")}
      >
        <LayoutGrid className="h-4 w-4" aria-hidden />
        Cards
      </Button>
      <Button
        type="button"
        variant={viewMode === "list" ? "secondary" : "ghost"}
        size="sm"
        className="gap-2"
        onClick={() => onViewChange("list")}
      >
        <List className="h-4 w-4" aria-hidden />
        List
      </Button>
    </div>
  );
}

export function KarunaPagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (size: KarunaPageSize) => void;
}) {
  if (totalItems === 0) {
    return null;
  }
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-4">
      <div className="flex flex-wrap items-center gap-4">
        <p className="text-sm text-muted-foreground">
          Showing {start}–{end} of {totalItems}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={value =>
              onPageSizeChange(Number(value) as KarunaPageSize)
            }
          >
            <SelectTrigger className="h-9 w-[5.5rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KARUNA_PAGE_SIZE_OPTIONS.map(option => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <span className="min-w-[7rem] text-center text-sm tabular-nums text-muted-foreground">
          Page {page} of {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
